import { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";

const PAGES = [
  { key: "dashboard",   label: "Resumen" },
  { key: "restaurants", label: "Restaurantes" },
  { key: "events",      label: "Eventos" },
  { key: "attendance",  label: "Asistencia" },
  { key: "invoices",    label: "Facturas" },
  { key: "budget",      label: "Presupuesto" },
  { key: "employees",   label: "Empleados" },
];

const normRole = (r) => (r === "operator" ? "editor" : r || "viewer");

export default function Users() {
  const [data, setData] = useState({ users: [], pending_allowlist: [] });
  const [allowOpen, setAllowOpen] = useState(false);
  const [allowForm, setAllowForm] = useState({ email: "", role: "editor", pages: PAGES.map(p => p.key) });
  const [editUser, setEditUser] = useState(null); // {user_id, email, role, allowed_pages}

  const load = () => api.get("/users").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const submitAllow = async () => {
    if (!allowForm.email) return toast.error("Introduce un email");
    if (allowForm.role !== "admin" && allowForm.pages.length === 0) {
      return toast.error("Selecciona al menos una pestaña, o asigna el rol Admin (acceso total).");
    }
    try {
      await api.post("/users/allow", {
        email: allowForm.email.trim().toLowerCase(),
        role: allowForm.role,
        allowed_pages: allowForm.role === "admin" ? null : allowForm.pages,
      });
      toast.success("Email añadido");
      setAllowOpen(false);
      setAllowForm({ email: "", role: "editor", pages: PAGES.map(p => p.key) });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const revoke = async (u) => {
    if (!window.confirm(`¿Revocar acceso a ${u.email}?`)) return;
    await api.post(`/users/${u.user_id}/revoke`); toast.success("Acceso revocado"); load();
  };

  const setRoleFor = async (u, r) => {
    await api.post(`/users/${u.user_id}/role`, { role: r });
    toast.success("Rol actualizado");
    load();
  };

  const openEditUser = (u) => {
    setEditUser({
      user_id: u.user_id,
      email: u.email,
      role: normRole(u.role),
      allowed_pages: (u.allowed_pages && u.allowed_pages.length > 0) ? u.allowed_pages : PAGES.map(p => p.key),
    });
  };

  const saveEditUser = async () => {
    if (editUser.role !== "admin" && editUser.allowed_pages.length === 0) {
      return toast.error("Selecciona al menos una pestaña.");
    }
    try {
      if (editUser.role !== normRole(data.users.find((x) => x.user_id === editUser.user_id)?.role)) {
        await api.post(`/users/${editUser.user_id}/role`, { role: editUser.role });
      }
      await api.post(`/users/${editUser.user_id}/permissions`, {
        allowed_pages: editUser.role === "admin" ? [] : editUser.allowed_pages,
      });
      toast.success("Permisos actualizados");
      setEditUser(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const togglePage = (form, setForm, key) => {
    const has = form.pages.includes(key);
    setForm({ ...form, pages: has ? form.pages.filter(k => k !== key) : [...form.pages, key] });
  };

  const togglePageEdit = (key) => {
    if (!editUser) return;
    const has = editUser.allowed_pages.includes(key);
    setEditUser({
      ...editUser,
      allowed_pages: has ? editUser.allowed_pages.filter(k => k !== key) : [...editUser.allowed_pages, key],
    });
  };

  const pagesSummary = (u) => {
    if (u.role === "admin") return "Todas (admin)";
    const pgs = u.allowed_pages || [];
    if (pgs.length === 0) return "Todas (sin restricción)";
    if (pgs.length === PAGES.length) return "Todas";
    return `${pgs.length} pestaña${pgs.length !== 1 ? "s" : ""}`;
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl" data-testid="users-page">
      <div className="mb-8">
        <p className="label-eyebrow">Acceso</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">Usuarios</h1>
        <p className="text-sm text-zinc-600 mt-3 max-w-2xl">Sólo los emails de aquí pueden iniciar sesión con Google. Para cada persona puedes elegir las pestañas a las que tiene acceso y si es Editor (puede modificar) o Viewer (solo lectura).</p>
        <div className="mt-4 grid grid-cols-3 gap-3 max-w-3xl text-xs">
          <div className="bg-white border border-zinc-200 p-3">
            <p className="font-semibold uppercase tracking-wider text-zinc-700">Admin</p>
            <p className="text-zinc-500 mt-1">Acceso total a todo, incluyendo Waves, Usuarios y Auditoría.</p>
          </div>
          <div className="bg-white border border-zinc-200 p-3">
            <p className="font-semibold uppercase tracking-wider text-zinc-700">Editor</p>
            <p className="text-zinc-500 mt-1">Puede modificar las pestañas que le asignes (crear/editar eventos, asistencia, facturas, etc.).</p>
          </div>
          <div className="bg-white border border-zinc-200 p-3">
            <p className="font-semibold uppercase tracking-wider text-zinc-700">Viewer</p>
            <p className="text-zinc-500 mt-1">Solo lectura: ve las pestañas que le asignes pero no puede modificar nada.</p>
          </div>
        </div>
      </div>

      <div className="mb-6 flex justify-end">
        <Button onClick={() => setAllowOpen(true)} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="open-allow">
          + Añadir persona
        </Button>
      </div>

      <section className="mb-10">
        <h2 className="font-heading text-2xl mb-3">Usuarios activos</h2>
        <div className="bg-white border border-zinc-200">
          <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500 font-semibold">
            <div className="col-span-4">Email</div>
            <div className="col-span-2">Nombre</div>
            <div className="col-span-2">Rol</div>
            <div className="col-span-2">Acceso</div>
            <div className="col-span-2 text-right">Acciones</div>
          </div>
          {data.users.length === 0 && <div className="p-6 text-sm text-zinc-500">Sin usuarios registrados.</div>}
          {data.users.map((u) => (
            <div key={u.user_id} className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 items-center text-sm">
              <div className="col-span-4 font-mono truncate">{u.email}</div>
              <div className="col-span-2">{u.name || "—"}</div>
              <div className="col-span-2">
                <Select value={normRole(u.role)} onValueChange={(r) => setRoleFor(u, r)}>
                  <SelectTrigger className="rounded-none h-8 text-xs" data-testid={`role-${u.user_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 text-xs">
                <span className="font-mono text-zinc-700">{pagesSummary(u)}</span>
              </div>
              <div className="col-span-2 text-right flex justify-end gap-3">
                {u.role !== "admin" && (
                  <button onClick={() => openEditUser(u)} className="text-xs uppercase tracking-wider text-zinc-700 hover:underline" data-testid={`edit-perms-${u.user_id}`}>Permisos</button>
                )}
                {u.allowed ? (
                  <button onClick={() => revoke(u)} className="text-xs uppercase tracking-wider text-red-600 hover:underline" data-testid={`revoke-${u.user_id}`}>Revocar</button>
                ) : (
                  <span className="text-xs uppercase tracking-wider text-zinc-400">Revocado</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-heading text-2xl mb-3">En lista de espera</h2>
        <div className="bg-white border border-zinc-200">
          {data.pending_allowlist.length === 0 && <div className="p-6 text-sm text-zinc-500">Ninguno.</div>}
          {data.pending_allowlist.map((p) => (
            <div key={p.email} className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-sm">
              <div className="col-span-7 font-mono">{p.email}</div>
              <div className="col-span-2 uppercase tracking-wider text-xs text-zinc-500">{p.role}</div>
              <div className="col-span-3 text-xs text-zinc-500">
                {p.allowed_pages?.length ? `${p.allowed_pages.length} pestañas` : "Todas"}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Allow new user dialog */}
      <Dialog open={allowOpen} onOpenChange={setAllowOpen}>
        <DialogContent className="rounded-none max-w-lg" data-testid="allow-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Añadir persona</DialogTitle>
            <DialogDescription>Solo los emails permitidos pueden iniciar sesión.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider">Email</Label>
              <Input
                value={allowForm.email}
                onChange={(e) => setAllowForm({ ...allowForm, email: e.target.value })}
                placeholder="persona@empresa.com"
                className="rounded-none"
                data-testid="allow-email"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Tipo de acceso</Label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                <button type="button" onClick={() => setAllowForm({ ...allowForm, role: "viewer" })} data-testid="allow-role-viewer"
                  className={`p-2.5 border-2 text-sm font-medium transition-colors ${allowForm.role === "viewer" ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}>
                  Viewer<br /><span className="text-[10px] font-normal text-zinc-500">solo ver</span>
                </button>
                <button type="button" onClick={() => setAllowForm({ ...allowForm, role: "editor" })} data-testid="allow-role-editor"
                  className={`p-2.5 border-2 text-sm font-medium transition-colors ${allowForm.role === "editor" ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}>
                  Editor<br /><span className="text-[10px] font-normal text-zinc-500">ver + modificar</span>
                </button>
                <button type="button" onClick={() => setAllowForm({ ...allowForm, role: "admin" })} data-testid="allow-role-admin"
                  className={`p-2.5 border-2 text-sm font-medium transition-colors ${allowForm.role === "admin" ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}>
                  Admin<br /><span className="text-[10px] font-normal text-zinc-500">todo</span>
                </button>
              </div>
            </div>
            {allowForm.role !== "admin" && (
              <div data-testid="allow-pages-block">
                <Label className="text-xs uppercase tracking-wider">Pestañas con acceso</Label>
                <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                  {PAGES.map((p) => {
                    const checked = allowForm.pages.includes(p.key);
                    return (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => togglePage(allowForm, setAllowForm, p.key)}
                        data-testid={`allow-page-${p.key}`}
                        className={`flex items-center gap-2 p-2 border text-sm transition-colors ${checked ? "border-emerald-500 bg-emerald-50" : "border-zinc-200 hover:border-zinc-400"}`}
                      >
                        <span className={`w-4 h-4 border-2 flex-shrink-0 flex items-center justify-center ${checked ? "border-emerald-600 bg-emerald-600 text-white text-[10px]" : "border-zinc-300"}`}>{checked && "✓"}</span>
                        <span>{p.label}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-zinc-500 mt-1.5">Las pestañas Waves, Usuarios y Auditoría son solo para administradores.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={submitAllow} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="allow-submit">Añadir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user permissions dialog */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="rounded-none max-w-lg" data-testid="edit-perms-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Editar permisos</DialogTitle>
            <DialogDescription>{editUser?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider">Tipo de acceso</Label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                <button type="button" onClick={() => setEditUser({ ...editUser, role: "viewer" })}
                  className={`p-2.5 border-2 text-sm font-medium ${editUser?.role === "viewer" ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}>
                  Viewer<br /><span className="text-[10px] font-normal text-zinc-500">solo ver</span>
                </button>
                <button type="button" onClick={() => setEditUser({ ...editUser, role: "editor" })}
                  className={`p-2.5 border-2 text-sm font-medium ${editUser?.role === "editor" ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}>
                  Editor<br /><span className="text-[10px] font-normal text-zinc-500">ver + modificar</span>
                </button>
                <button type="button" onClick={() => setEditUser({ ...editUser, role: "admin" })}
                  className={`p-2.5 border-2 text-sm font-medium ${editUser?.role === "admin" ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}>
                  Admin<br /><span className="text-[10px] font-normal text-zinc-500">todo</span>
                </button>
              </div>
            </div>
            {editUser && editUser.role !== "admin" && (
              <div>
                <Label className="text-xs uppercase tracking-wider">Pestañas con acceso</Label>
                <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                  {PAGES.map((p) => {
                    const checked = editUser.allowed_pages.includes(p.key);
                    return (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => togglePageEdit(p.key)}
                        data-testid={`edit-page-${p.key}`}
                        className={`flex items-center gap-2 p-2 border text-sm ${checked ? "border-emerald-500 bg-emerald-50" : "border-zinc-200 hover:border-zinc-400"}`}
                      >
                        <span className={`w-4 h-4 border-2 flex-shrink-0 flex items-center justify-center ${checked ? "border-emerald-600 bg-emerald-600 text-white text-[10px]" : "border-zinc-300"}`}>{checked && "✓"}</span>
                        <span>{p.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setEditUser(null)} variant="outline" className="rounded-none">Cancelar</Button>
            <Button onClick={saveEditUser} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="edit-perms-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
